import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { reviewKeys, reviewPlatformKeys } from '@/lib/queryKeys';
import type { SetAutoPostDto } from '@/types/review-platform';

/**
 * Turn auto-post on or off for one platform (NOT-576 §4.3).
 *
 * Invalidates the auto-post preview as well as the platform list: switching
 * this changes the verdict on every pending draft, and leaving the preview
 * stale would show someone "Held — auto-post is off for this platform"
 * seconds after they turned it on.
 */
export function useSetPlatformAutoPost(locationId: string | null = null) {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, SetAutoPostDto>({
    mutationFn: (dto) =>
      apiPatch<unknown, SetAutoPostDto>(
        operationsClient,
        reviewPlatformsV2Endpoints.setAutoPost(),
        dto,
      ),
    onSuccess: (_, dto) => {
      void queryClient.invalidateQueries({
        queryKey: reviewPlatformKeys.settings(locationId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.autoPostPending(locationId),
      });
      toast.success(
        dto.value
          ? 'Auto-post is on. Drafts go out after the delay unless you stop them.'
          : 'Auto-post is off. Replies now wait for someone to post them.',
      );
    },
  });
}

/**
 * The kill switch: stop or resume every pending auto-post on this settings row.
 *
 * Touches no per-platform opt-in, so releasing it resumes exactly what was
 * running before — which is what makes pulling it a safe reflex rather than a
 * decision someone has to reconstruct afterwards.
 *
 * `locationId` goes in the BODY, not just the cache key. The sweep reads the
 * pause off the settings row each platform hangs from, so a pause that omits
 * it writes the tenant-default row: the screen keeps reading its own row and
 * shows nothing changed, while auto-post carries on posting.
 */
export function useSetAutoPostPaused(locationId: string | null = null) {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, { value: boolean }>({
    mutationFn: (dto) =>
      apiPatch<unknown, { value: boolean; locationId?: string }>(
        operationsClient,
        reviewPlatformsV2Endpoints.setAutoPostPaused(),
        locationId ? { ...dto, locationId } : dto,
      ),
    onSuccess: (_, dto) => {
      void queryClient.invalidateQueries({
        queryKey: reviewPlatformKeys.settings(locationId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.autoPostPending(locationId),
      });
      toast.success(
        dto.value
          ? 'Auto-post paused. Nothing will go out until you resume it.'
          : 'Auto-post resumed.',
      );
    },
  });
}

/**
 * Apply the reply choice as one request.
 *
 * This replaces a pair of independent PATCHes for `writeEnabled` and
 * `autoPostEnabled`. Either could fail alone, and the leftovers were not
 * neutral — the worst left auto-post armed on a platform the card then
 * rendered as "off", so switching replies back on later would start posting
 * automatically without anyone choosing it again.
 */
export function useSetReplyMode(locationId: string | null = null) {
  const queryClient = useQueryClient();
  return useApiMutation<
    unknown,
    { platformConfigId: string; mode: 'off' | 'manual' | 'automatic'; delayMinutes?: number }
  >({
    mutationFn: (dto) =>
      apiPatch(operationsClient, reviewPlatformsV2Endpoints.setReplyMode(), dto),
    onSuccess: (_, dto) => {
      void queryClient.invalidateQueries({
        queryKey: reviewPlatformKeys.settings(locationId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.autoPostPending(locationId),
      });
      toast.success(
        dto.mode === 'automatic'
          ? 'Auto-post is on. Drafts go out after the delay unless you stop them.'
          : dto.mode === 'manual'
            ? 'Drafts will wait here until someone sends them.'
            : 'Notifi will not post replies to this platform.',
      );
    },
  });
}
